/**
 * SoloGame — the original solo game view.
 *
 * Extracted from App.jsx with zero logic changes.
 * Full-bleed graph with floating HUD panels.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useGame from '../hooks/useGame';
import useSound from '../hooks/useSound';
import useTutorial from '../hooks/useTutorial';
import WordGraph from './WordGraph';
import VictoryModal from './VictoryModal';
import GraphSearch from './GraphSearch';
import HowToPlay from './HowToPlay';
import { findShortestPath, pathToEdgeSet } from '../utils/graph';

export default function SoloGame({ onBack }) {
  const {
    nodes, links, selectedWord, lastExpandedWord, expansionFocusIds, lastCombineResult,
    targetWord, initialWord,
    expansions, gameComplete,
    potentialScore, scoreData,
    difficultyMultiplier, difficultyPercentile,
    victoryPending, winningWord,
    loading, error,
    warmth, closestSimilarity, initialSimilarity,
    startNewGame, handleNodeClick, combineWords,
  } = useGame();

  const sound = useSound();
  const tutorial = useTutorial();

  const [showHow, setShowHow] = useState(false);
  const [searchedWord, setSearchedWord] = useState(null);
  const [difficulty, setDifficulty] = useState(50);
  const [gameStarted, setGameStarted] = useState(false);
  const [showVictoryModal, setShowVictoryModal] = useState(true);
  const [pathRevealed, setPathRevealed] = useState(false);
  const prevWarmth = useRef(0);

  const handleNewGame = useCallback(() => {
    startNewGame(difficulty);
    setGameStarted(true);
    setShowVictoryModal(true);
    setPathRevealed(false);
    tutorial.reset();
  }, [startNewGame, difficulty, tutorial]);

  const handleViewGraph = useCallback(() => {
    setShowVictoryModal(false);
  }, []);

  // Wrapped handlers for sound + tutorial integration
  const onNodeClick = useCallback((word) => {
    handleNodeClick(word);
    sound.playExpand();
    tutorial.onExpand();
  }, [handleNodeClick, sound, tutorial]);

  const onCombine = useCallback((w1, w2) => {
    combineWords(w1, w2);
    sound.playCombine();
    tutorial.onCombine();
  }, [combineWords, sound, tutorial]);

  // Play warmth sound when getting significantly closer
  useEffect(() => {
    if (warmth - prevWarmth.current > 0.15) {
      sound.playWarmth();
    }
    prevWarmth.current = warmth;
  }, [warmth, sound]);

  // Play victory sound
  useEffect(() => {
    if (victoryPending) sound.playVictory();
  }, [victoryPending, sound]);

  // Show shortest path when game complete + path has been revealed
  // (during reveal phase before modal, AND after modal is dismissed)
  const shortestPathEdges = useMemo(() => {
    if (!gameComplete || !pathRevealed) return null;
    const start = initialWord?.word;
    const target = targetWord?.word;
    if (!start || !target) return null;
    const path = findShortestPath(start, target, links);
    return path ? pathToEdgeSet(path) : null;
  }, [gameComplete, pathRevealed, initialWord, targetWord, links]);

  // Path reveal: when game completes, show path for 1.5s before showing modal
  useEffect(() => {
    if (!gameComplete || pathRevealed) return;
    setShowVictoryModal(false); // hide modal initially
    setPathRevealed(true); // show the path
    const timer = setTimeout(() => {
      setShowVictoryModal(true); // show modal after delay
    }, 1500);
    return () => clearTimeout(timer);
  }, [gameComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear search highlight on any graph interaction (expand/combine)
  useEffect(() => { setSearchedWord(null); }, [expansionFocusIds]);

  const handleSearch = useCallback((word) => {
    setSearchedWord(word || null);
  }, []);

  const hasNodes = nodes.length > 0;
  const showHUD = hasNodes && !gameComplete;
  const showPostGameControls = gameComplete && !showVictoryModal;

  // Difficulty label for the picker
  const diffLabel =
    difficulty <= 20 ? 'Easy' :
    difficulty <= 40 ? 'Moderate' :
    difficulty <= 60 ? 'Medium' :
    difficulty <= 80 ? 'Hard' :
    'Extreme';

  // Show difficulty picker before first game
  if (!gameStarted) {
    return (
      <div className="landing">
        <div className="landing-card">
          <h1 className="landing-title"><span className="logo-l">L</span><span className="logo-e">e</span><span className="logo-x">x</span><span className="logo-i">i</span><span className="logo-quest">Quest</span></h1>
          <div className="landing-stripe" />
          <p className="landing-subtitle">Navigate the semantic space between words</p>

          <div className="difficulty-picker">
            <h2 className="difficulty-picker-label">Select Difficulty</h2>
            <div className="difficulty-picker-row">
              <span className="difficulty-picker-easy">Easy</span>
              <input
                type="range"
                className="difficulty-slider difficulty-slider-lg"
                min={1}
                max={100}
                value={difficulty}
                onChange={e => setDifficulty(Number(e.target.value))}
              />
              <span className="difficulty-picker-hard">Hard</span>
            </div>
            <div className="difficulty-picker-info">
              <span className="difficulty-picker-level">{diffLabel} ({difficulty})</span>
              <span className="difficulty-picker-mult">
                {difficulty > 50 ? `+${Math.round((0.5 + 1.5 * ((difficulty / 100) ** 1.3) - 1) * 100)}% score bonus` :
                 difficulty < 50 ? `${Math.round((0.5 + 1.5 * ((difficulty / 100) ** 1.3) - 1) * 100)}% score` :
                 '1x score'}
              </span>
            </div>
          </div>

          <div className="landing-buttons">
            <button className="btn-landing btn-solo" onClick={handleNewGame} disabled={loading}>
              {loading ? 'Starting...' : 'Start Game'}
            </button>
            <button className="btn-landing btn-leave" onClick={onBack}>
              {'\u2190'} Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Recent panel: for expands, show new words; for combines, show the result word
  const recentSource = lastCombineResult
    ? `${lastCombineResult.parents[0]} + ${lastCombineResult.parents[1]}`
    : lastExpandedWord;
  const recentWords = lastCombineResult
    ? lastCombineResult.results
    : expansionFocusIds
      ? [...expansionFocusIds].filter(id => id !== lastExpandedWord)
      : [];

  return (
    <div className="app">
      {/* ── Full-bleed graph ── */}
      <div className="graph-viewport">
        {error && (
          <div className="toast-error">{error}</div>
        )}

        {loading && !hasNodes && (
          <div className="loading-screen">
            <div className="loading-orbit" />
            <div className="loading-label">Mapping semantic space...</div>
          </div>
        )}

        {hasNodes && (
          <WordGraph
            nodes={nodes}
            links={links}
            onNodeClick={onNodeClick}
            onCombine={onCombine}
            selectedWord={selectedWord}
            targetWord={targetWord?.word}
            winningWord={winningWord}
            victoryPending={victoryPending}
            initialWord={initialWord?.word}
            lastExpandedWord={lastExpandedWord}
            expansionFocusIds={expansionFocusIds}
            lastCombineResult={lastCombineResult}
            searchedWord={searchedWord}
            loading={loading}
            shortestPathEdges={shortestPathEdges}
            warmth={warmth}
          />
        )}
      </div>

      {/* ── Header bar ── */}
      <header className="hud-header">
        <div className="hud-logo">
          <span className="logo-l">L</span><span className="logo-e">e</span><span className="logo-x">x</span><span className="logo-i">i</span><span className="logo-quest">Quest</span>
          <button className="help-trigger" onClick={() => setShowHow(true)} aria-label="How to play">?</button>
        </div>

        {targetWord && (
          <div className="target-beacon">
            <span className="target-label">find</span>
            <span className="target-word">{(targetWord.word || '').replace(/\b\w/g, c => c.toUpperCase())}{targetWord.emoji ? ` ${targetWord.emoji}` : ''}</span>
          </div>
        )}

        <div className="hud-header-right">
          {hasNodes && <GraphSearch nodes={nodes} onSearch={handleSearch} />}
          <div className="hud-stats">
            <div className="stat-group">
              <span className="stat-num">{expansions}</span>
              <span className="stat-lbl">moves</span>
            </div>
            <div className="stat-sep" />
            <div className="stat-group">
              <span className="stat-num score-val">{potentialScore}</span>
              <span className="stat-lbl">score</span>
            </div>
            {hasNodes && !gameComplete && (
              <>
                <div className="stat-sep" />
                <div className="stat-group warmth-stat" title={`${Math.round(warmth * 100)}% proximity to target`}>
                  <span className="stat-num warmth-val">
                    <span className="warmth-dot" style={{
                      background: warmth < 0.33 ? 'var(--blue)' : warmth < 0.66 ? 'var(--gold)' : 'var(--red)',
                      boxShadow: `0 0 6px ${warmth < 0.33 ? 'var(--blue-glow)' : warmth < 0.66 ? 'var(--gold-glow)' : 'var(--red-glow)'}`,
                    }} />
                    {Math.round(warmth * 100)}%
                  </span>
                  <span className="stat-lbl">warmth</span>
                </div>
              </>
            )}
            <button
              className={`btn-sound ${sound.muted ? 'muted' : ''}`}
              onClick={sound.toggleMute}
              aria-label={sound.muted ? 'Unmute sounds' : 'Mute sounds'}
              title={sound.muted ? 'Enable sound' : 'Mute sound'}
            >
              {sound.muted ? '\u{1F507}' : '\u{1F50A}'}
            </button>
            <button className="btn-new" onClick={handleNewGame} disabled={loading}>
              New Game
            </button>
          </div>
        </div>
      </header>

      {/* Difficulty slider — between stats and graph */}
      <div className="difficulty-bar">
        <label className="difficulty-label">
          <span className="difficulty-text">Difficulty</span>
          <input
            type="range"
            className="difficulty-slider difficulty-slider-sm"
            min={1}
            max={100}
            value={difficulty}
            onChange={e => setDifficulty(Number(e.target.value))}
          />
          <span className="difficulty-value">{difficulty}</span>
        </label>
        {difficultyMultiplier !== 1.0 && (
          <span className={`difficulty-mult ${difficultyMultiplier > 1 ? 'bonus' : 'penalty'}`}>
            {difficultyMultiplier > 1 ? '+' : ''}{Math.round((difficultyMultiplier - 1) * 100)}% score
          </span>
        )}
      </div>

      {showHow && <HowToPlay onClose={() => setShowHow(false)} variant="solo" />}

      {/* Back button — bottom left */}
      <button className="btn-back" onClick={onBack}>
        {'\u2190'} Menu
      </button>

      {/* Legend — bottom right */}
      {showHUD && (
        <div className="hud-legend">
          <div className="legend-item"><span className="legend-swatch start" /><span>Start</span></div>
          <div className="legend-item"><span className="legend-swatch expanded" /><span>Explored</span></div>
          <div className="legend-item"><span className="legend-swatch fresh" /><span>New</span></div>
          <div className="legend-item"><span className="legend-swatch target" /><span>Target</span></div>
        </div>
      )}

      {/* Recent words — top left, below logo */}
      {showHUD && recentWords.length > 0 && (
        <div className="hud-recent">
          <div className="recent-header">
            <span className="recent-source">{recentSource}</span>
            <span className="recent-arrow">{'\u2192'}</span>
          </div>
          <div className="recent-words">
            {recentWords.map(word => (
              <span key={word} className="recent-word">{word}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── Post-game floating controls ── */}
      {showPostGameControls && (
        <div className="post-game-controls">
          <button className="btn-new" onClick={handleNewGame} disabled={loading}>
            Play Again
          </button>
          <button className="btn-new" onClick={() => setShowVictoryModal(true)}>
            View Score
          </button>
        </div>
      )}

      {/* ── Post-game legend ── */}
      {showPostGameControls && (
        <div className="hud-legend">
          <div className="legend-item"><span className="legend-swatch shortest-path" /><span>Shortest Path</span></div>
          <div className="legend-item"><span className="legend-swatch start" /><span>Start</span></div>
          <div className="legend-item"><span className="legend-swatch target" /><span>Target</span></div>
        </div>
      )}

      {/* ── Tutorial tooltips ── */}
      {tutorial.showClickHint && hasNodes && !gameComplete && (
        <div className="tutorial-tooltip tutorial-click">
          <span>Click the start word to explore its neighbors</span>
          <button className="tutorial-dismiss" onClick={tutorial.dismiss}>{'\u00d7'}</button>
        </div>
      )}
      {tutorial.showCombineHint && hasNodes && !gameComplete && (
        <div className="tutorial-tooltip tutorial-combine">
          <span>Drag one word onto another to combine them</span>
          <button className="tutorial-dismiss" onClick={tutorial.dismiss}>{'\u00d7'}</button>
        </div>
      )}

      {/* ── Victory ── */}
      {gameComplete && scoreData && showVictoryModal && (
        <VictoryModal
          scoreData={scoreData}
          expansions={expansions}
          onNewGame={handleNewGame}
          onViewGraph={handleViewGraph}
          targetWord={targetWord?.word}
          initialWord={initialWord?.word}
        />
      )}
    </div>
  );
}
